import { useRef } from 'react';
import { QuestionError } from '@sm/question-definitions';
import { ExternalValidator } from '@sm/question-widgets/respondent-survey';
import { ValidationManager, ValidationMap, ValidationResult } from './types';

function useValidationManager(): ValidationManager {
  // persistent value with no re-render
  const validators = useRef<ValidationMap>({});

  /**
   * Registers validator functions with a question
   * @param questionId A reference ID
   * @param validatorFns The validator functions
   */
  const registerValidator = (questionId: string, validatorFns: ExternalValidator[]): void => {
    validators.current = {
      ...(validators.current || {}),
      [questionId]: validatorFns,
    };
  };

  /**
   * Unregister a reference
   * @param questionId A reference ID
   */
  const unregisterValidator = (questionId: string): void => {
    if (validators.current[questionId]) {
      delete validators.current[questionId];
    }
  };

  /**
   * Run validators for a question
   * @param questionId The question to validate
   * @param force Force non-touched to validate
   * @returns {ValidationResult}
   */
  const validate = (questionId: string, force = false): ValidationResult => {
    const questionValidator = validators.current[questionId] || [];
    const errors = questionValidator.reduce<QuestionError[]>((prevErrors, validator) => {
      return [...prevErrors, ...(validator(force) || [])];
    }, []);
    return { isValid: !errors.length, errors };
  };

  /**
   * Runs all validators for all questions
   * @returns {ValidationResult}
   */
  const runValidators = (): ValidationResult => {
    const errors = Object.keys(validators.current).reduce<QuestionError[]>((prevErrors, questionId) => {
      const { errors: questionErrors } = validate(questionId, true);
      return [...prevErrors, ...questionErrors];
    }, []);
    return { isValid: !errors.length, errors };
  };

  return { validators: validators.current, registerValidator, unregisterValidator, validate, runValidators };
}

export default useValidationManager;
